import { JogButton } from '@/components/jog/JogButton';
import { useJog } from '@/components/jog/useJog';
import { useShowJogPadDeco } from '@/store/store';
import { AXIS_INDEX } from '@/types/machine.types';
import styles from '@/components/jog/jog.module.css';

// Corner decoration triangles (point toward center) -------------------------
const CORNERS = [
  { x: 52, y: 52, rot: 315 },
  { x: 188, y: 52, rot: 45 },
  { x: 52, y: 188, rot: 225 },
  { x: 188, y: 188, rot: 135 },
];

/** "Jog Control" heading + analog X/Y pad + glassy Z column. Self-contained / reusable. */
export function JogControls(): JSX.Element {
  const { estop, onStart, onStop } = useJog();
  const showDeco = useShowJogPadDeco();

  return (
    <div className={styles.jogControls}>
      <span className={styles.jogSectionTitle}>Jog Control</span>
      <div className={styles.jogPadRow}>
        {/* Analog jog pad — X horizontal, Y vertical */}
        <div className={styles.analogPad}>
          <svg className={styles.analogPadDeco} viewBox="0 0 240 240" aria-hidden="true">
            {/* Outer ring is always shown; corner triangles are the toggleable deco. */}
            <circle
              cx="120"
              cy="120"
              r="119.5"
              fill="none"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="0.5"
            />
            {showDeco &&
              CORNERS.map((c, i) => (
                <polygon
                  key={i}
                  points="0,-15.075 24.355,15.075 -24.355,15.075"
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  transform={`translate(${c.x} ${c.y}) rotate(${c.rot})`}
                />
              ))}
          </svg>

          <JogButton
            label="X+"
            axisIndex={AXIS_INDEX.X}
            direction={1}
            disabled={estop}
            className={`${styles.analogBtn} ${styles.analogBtnRight}`}
            onStart={onStart}
            onStop={onStop}
          />
          <JogButton
            label="X-"
            axisIndex={AXIS_INDEX.X}
            direction={-1}
            disabled={estop}
            className={`${styles.analogBtn} ${styles.analogBtnLeft}`}
            onStart={onStart}
            onStop={onStop}
          />
          <JogButton
            label="Y-"
            axisIndex={AXIS_INDEX.Y}
            direction={-1}
            disabled={estop}
            className={`${styles.analogBtn} ${styles.analogBtnDown}`}
            onStart={onStart}
            onStop={onStop}
          />
          <JogButton
            label="Y+"
            axisIndex={AXIS_INDEX.Y}
            direction={1}
            disabled={estop}
            className={`${styles.analogBtn} ${styles.analogBtnUp}`}
            onStart={onStart}
            onStop={onStop}
          />
          <div className={styles.analogCenter}>Y/X</div>
        </div>

        {/* Z axis column */}
        <div className={styles.zAxisCol}>
          <JogButton
            label="Z+"
            axisIndex={AXIS_INDEX.Z}
            direction={1}
            disabled={estop}
            className={styles.zAxisBtn}
            onStart={onStart}
            onStop={onStop}
          >
            <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
              <polygon points="13,0 26,16 0,16" fill="#9747ff" />
            </svg>
            <span className={styles.zAxisLabel}>Z+</span>
          </JogButton>
          <span className={styles.zAxisLine} aria-hidden="true" />
          <JogButton
            label="Z-"
            axisIndex={AXIS_INDEX.Z}
            direction={-1}
            disabled={estop}
            className={styles.zAxisBtn}
            onStart={onStart}
            onStop={onStop}
          >
            <span className={styles.zAxisLabel}>Z-</span>
            <svg width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
              <polygon points="0,0 26,0 13,16" fill="#9747ff" />
            </svg>
          </JogButton>
        </div>
      </div>
    </div>
  );
}
